import React, { useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSpring, animated, config } from "react-spring";
import ReactPlayer from "react-player";
import {
    FaChevronLeft,
    FaChevronRight,
    FaArrowRight,
    FaBolt,
    FaChargingStation,
    FaTachometerAlt,
    FaCogs,
    FaTint,
    FaFeatherAlt,
    FaChartLine,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";

// Demo showcase content — reuses the existing prototype videos
const DEMOS = [
    {
        id: "01",
        title: "Axial Flux Motor Prototype",
        video: "/axialisvideo/VID-1.mp4",
        description:
            "Axialis is pioneering a revolutionary liquid-cooled yokeless axial flux motor technology that fundamentally transforms electric propulsion. Traditional radial flux motors have remained largely unchanged for over 200 years. Our axial flux approach delivers superior power density with significantly reduced weight.",
    },
    {
        id: "02",
        title: "Integrated Liquid Cooling",
        video: "/axialisvideo/VID-2.mp4",
        description:
            "Electric vehicles currently use radial flux electric motors for propulsion. These motors, conceptualized more than 200 years ago, have fundamental limitations. Axialis is developing next-generation axial flux motor technology with integrated liquid cooling that will revolutionize electric propulsion.",
    },
    {
        id: "03",
        title: "Superior Power Density",
        video: "/axialisvideo/VID-4.mp4",
        description:
            "Our axial flux technology offers significant advantages over conventional motor designs. The innovative approach delivers unprecedented power density, efficiency, and thermal management while reducing weight and complexity.",
    },
];

const SPECS = [
    { icon: FaBolt, value: "71 kW", label: "Continuous Power", sub: "(at 5000 RPM)" },
    { icon: FaChargingStation, value: "150 kW", label: "Peak Power" },
    { icon: FaTachometerAlt, value: "8,000 RPM", label: "Maximum Speed" },
];

const FEATURES = [
    { icon: FaCogs, title: "Revolutionary Design", text: "Yokeless and compact for maximum power density." },
    { icon: FaTint, title: "Liquid-Cooled", text: "Advanced cooling for optimal performance." },
    { icon: FaFeatherAlt, title: "Lightweight", text: "Significantly lighter than traditional motors." },
    { icon: FaChartLine, title: "High Efficiency", text: "Engineered for superior efficiency and reliability." },
];

// Full prototype specification — rendered as a semantic table so the data is
// readable by search engines and AI crawlers, and mirrored in Product JSON-LD.
const SPEC_TABLE = [
    { name: "Motor type", value: "Yokeless axial flux, dual rotor" },
    { name: "Continuous power", value: "71 kW (at 5,000 RPM)" },
    { name: "Peak power", value: "150 kW" },
    { name: "Maximum speed", value: "8,000 RPM" },
    { name: "Cooling", value: "Integrated direct liquid cooling" },
    { name: "Stator", value: "SMC (Soft Magnetic Composite) cores, concentrated windings with rectangular copper wire" },
    { name: "Target applications", value: "Automotive and electric vehicle traction" },
    { name: "Status", value: "Prototype — EU-funded development (2024–2026)" },
];

// Deliberately NOT typed as schema.org Product: Google requires offers/review/
// aggregateRating on Product markup, none of which honestly apply to a research
// prototype that is not for sale. WebPage + about keeps the specs machine-readable
// for AI crawlers without triggering Google's product-snippet validation.
const PAGE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://axialis.hr/explore-motor",
    "url": "https://axialis.hr/explore-motor",
    "name": "Axialis Yokeless Axial Flux Motor Prototype — Demos and Specifications",
    "publisher": { "@id": "https://axialis.hr/#organization" },
    "primaryImageOfPage": "https://axialis.hr/images/home-bg.jpg",
    "about": {
        "@type": "Thing",
        "name": "Axialis Yokeless Axial Flux Motor (Prototype)",
        "description":
            "Liquid-cooled yokeless axial flux electric motor prototype by Axialis: 71 kW continuous power at 5,000 RPM, 150 kW peak power, 8,000 RPM maximum speed, dual-rotor topology with SMC stator cores and integrated direct liquid cooling, developed for automotive and EV traction applications. Developed within the EU-funded project \"Development of New Axial Flux Electric Motor Technology\" (2024–2026).",
        "additionalProperty": SPEC_TABLE.map((row) => ({
            "@type": "PropertyValue",
            "name": row.name,
            "value": row.value,
        })),
    },
};

const ExploreMotor = () => {
    const [headerRef, headerInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [showcaseRef, showcaseInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [ctaRef, ctaInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [activeSlide, setActiveSlide] = useState(0);
    const [slideDirection, setSlideDirection] = useState(0); // -1 for left, 1 for right
    const totalSlides = DEMOS.length;

    const nextSlide = () => {
        setSlideDirection(1);
        setActiveSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
        setSlideDirection(-1);
        setActiveSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
    };

    const goToSlide = (index) => {
        setSlideDirection(index > activeSlide ? 1 : -1);
        setActiveSlide(index);
    };

    const headerAnimation = useSpring({
        opacity: headerInView ? 1 : 0,
        transform: headerInView ? "translateY(0px)" : "translateY(30px)",
        config: config.gentle,
    });

    const showcaseAnimation = useSpring({
        opacity: showcaseInView ? 1 : 0,
        transform: showcaseInView ? "translateY(0px)" : "translateY(40px)",
        config: config.gentle,
    });

    // Re-runs on every slide change: content glides in from the travel direction
    const slideAnim = useSpring({
        from: { opacity: 0, transform: `translateX(${slideDirection >= 0 ? 32 : -32}px)` },
        to: { opacity: 1, transform: "translateX(0px)" },
        reset: true,
        config: { tension: 170, friction: 26 },
    });

    const demo = DEMOS[activeSlide];

    return (
        <div className="font-sans overflow-hidden relative">
            <SEO
                title="Axial Flux Motor Prototype Specs - 71 kW Continuous, 150 kW Peak | Axialis"
                description="Axialis yokeless axial flux motor prototype: 71 kW continuous power at 5,000 RPM, 150 kW peak, 8,000 RPM max speed, dual rotors, SMC stator cores and integrated direct liquid cooling. Watch it in action."
                keywords="motor demo, axial flux video, electric motor performance, 71kW continuous power, 150kW peak power, 8000 RPM, Axialis technology"
                path="/explore-motor"
            />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(PAGE_SCHEMA)}</script>
            </Helmet>

            {/* Dark hero band — hard horizontal cut against the white showcase below */}
            <section ref={headerRef} className="relative bg-[#0b0d10] pt-28 pb-14 md:pt-36 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0b0d10] via-[#10141a] to-[#181e26]"></div>
                <div className="absolute -top-32 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <animated.div style={headerAnimation} className="flex flex-col md:flex-row items-center gap-10 md:gap-14">
                        <div className="w-full md:w-1/2">
                            <p className="text-white/50 text-xs font-medium tracking-[0.3em] uppercase mb-4">Prototypes</p>
                            <h1 className="text-4xl md:text-6xl font-sans font-bold tracking-tight text-white mb-6 leading-tight">
                                Explore Our Motor
                            </h1>
                            <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md">
                                Discover our axial flux motor prototypes and the technology that sets a new standard for efficiency and performance.
                            </p>
                        </div>
                        <div className="w-full md:w-1/2">
                            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 aspect-video">
                                <video
                                    src="/axialisvideo/VID-1.mp4"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </animated.div>
                </div>
            </section>

            {/* White showcase — thumbnail rail + demo card */}
            <section ref={showcaseRef} className="bg-white relative">
                <div className="container mx-auto px-4 py-10 md:py-16">
                    <animated.div style={showcaseAnimation} className="flex flex-col lg:flex-row gap-6 lg:gap-10">
                        {/* Thumbnail rail */}
                        <div className="flex lg:flex-col items-center justify-center lg:justify-start gap-4 lg:w-28 flex-shrink-0 order-2 lg:order-1">
                            {DEMOS.map((d, i) => (
                                <button
                                    key={d.id}
                                    onClick={() => goToSlide(i)}
                                    className="flex items-center gap-3 group"
                                    aria-label={`Show demo: ${d.title}`}
                                >
                                    <span className={`hidden lg:block text-sm transition-colors duration-300 ${i === activeSlide ? "text-[#212529] font-semibold" : "text-[#212529]/40"}`}>
                                        {d.id}
                                    </span>
                                    <span className={`block w-16 h-16 rounded-xl overflow-hidden border-2 transition-all duration-300 ${i === activeSlide ? "border-[#212529] shadow-md" : "border-gray-200 opacity-60 group-hover:opacity-100"}`}>
                                        <video
                                            src={`${d.video}#t=0.5`}
                                            muted
                                            playsInline
                                            preload="metadata"
                                            className="w-full h-full object-cover pointer-events-none"
                                        />
                                    </span>
                                </button>
                            ))}
                            <div className="hidden lg:block pt-4 mt-2 border-t border-gray-200 w-full text-center text-xs text-[#212529]/50">
                                <span className="font-semibold text-[#212529]">{demo.id}</span> / {String(totalSlides).padStart(2, "0")}
                            </div>
                        </div>

                        {/* Demo card */}
                        <div className="flex-1 min-w-0 order-1 lg:order-2 bg-white border border-gray-200 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] overflow-hidden">
                            <animated.div key={activeSlide} style={slideAnim} className="flex flex-col lg:flex-row gap-8 p-6 md:p-10 h-full">
                                {/* Text side */}
                                <div className="lg:w-1/2 flex flex-col">
                                    <p className="text-[#212529]/40 text-xs font-medium tracking-[0.3em] uppercase mb-3">
                                        Demo {activeSlide + 1}
                                    </p>
                                    <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight text-[#212529] mb-6 leading-tight">
                                        {demo.title}
                                    </h2>
                                    <p className="text-[#212529]/70 leading-relaxed mb-8">
                                        {demo.description}
                                    </p>
                                    <div className="flex flex-wrap gap-x-8 gap-y-4 mb-8">
                                        {SPECS.map((spec) => (
                                            <div key={spec.label} className="flex items-center gap-3">
                                                <span className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-[#212529] flex-shrink-0">
                                                    <spec.icon className="text-sm" />
                                                </span>
                                                <span>
                                                    <span className="block text-sm font-bold text-[#212529]">{spec.value}</span>
                                                    <span className="block text-xs text-[#212529]/50">
                                                        {spec.label}
                                                        {spec.sub ? <span className="block">{spec.sub}</span> : null}
                                                    </span>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-auto">
                                        <Link
                                            to="/technology"
                                            className="inline-flex items-center gap-3 bg-[#111418] hover:bg-black text-white text-sm font-medium px-6 py-3 rounded-lg transition-all duration-300 group"
                                        >
                                            View Full Specifications
                                            <FaChevronRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                                        </Link>
                                    </div>
                                </div>

                                {/* Media side */}
                                <div className="lg:w-1/2 relative bg-[#f4f5f7] rounded-xl p-4 flex items-center justify-center min-h-[420px]">
                                    <span className="absolute top-4 right-4 z-20 bg-white/90 text-[#212529] text-xs font-medium px-3 py-1 rounded-full shadow">
                                        {activeSlide + 1} / {totalSlides}
                                    </span>
                                    <div className="w-full max-w-[280px] aspect-[9/16] rounded-lg overflow-hidden shadow-lg bg-black/5">
                                        <ReactPlayer
                                            url={demo.video}
                                            width="100%"
                                            height="100%"
                                            playing={false}
                                            muted={false}
                                            controls={true}
                                            volume={0.8}
                                            playsinline
                                            config={{
                                                file: {
                                                    attributes: {
                                                        controlsList: "nodownload",
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="absolute bottom-4 right-4 z-20 flex gap-2">
                                        <button
                                            onClick={prevSlide}
                                            className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-[#212529] hover:bg-gray-50 transition-colors duration-300"
                                            aria-label="Previous demo"
                                        >
                                            <FaChevronLeft className="text-sm" />
                                        </button>
                                        <button
                                            onClick={nextSlide}
                                            className="w-10 h-10 rounded-full bg-[#111418] shadow flex items-center justify-center text-white hover:bg-black transition-colors duration-300"
                                            aria-label="Next demo"
                                        >
                                            <FaChevronRight className="text-sm" />
                                        </button>
                                    </div>
                                </div>
                            </animated.div>
                        </div>
                    </animated.div>
                </div>

                {/* Features strip */}
                <div className="border-t border-gray-200">
                    <div className="container mx-auto px-4 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {FEATURES.map((feature) => (
                            <div key={feature.title} className="flex items-start gap-4">
                                <span className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-[#212529] flex-shrink-0">
                                    <feature.icon className="text-sm" />
                                </span>
                                <span>
                                    <span className="block text-sm font-semibold text-[#212529] mb-0.5">{feature.title}</span>
                                    <span className="block text-xs text-[#212529]/60 leading-relaxed">{feature.text}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Prototype specification table — semantic HTML so the numbers are
                crawlable, not just decoration in the demo card above */}
            <section className="bg-white border-t border-gray-200">
                <div className="container mx-auto px-4 py-12 md:py-16">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight text-[#212529] mb-3">
                            Prototype Specifications
                        </h2>
                        <p className="text-[#212529]/60 mb-8 leading-relaxed">
                            Current performance figures for the Axialis yokeless axial flux motor
                            prototype, developed within the EU-funded project "Development of New
                            Axial Flux Electric Motor Technology" (2024–2026).
                        </p>
                        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                            <table className="w-full text-left text-sm">
                                <caption className="sr-only">
                                    Axialis axial flux motor prototype specifications
                                </caption>
                                <tbody>
                                    {SPEC_TABLE.map((row, i) => (
                                        <tr key={row.name} className={i % 2 === 0 ? "bg-gray-50/60" : "bg-white"}>
                                            <th scope="row" className="px-5 py-3.5 font-semibold text-[#212529] whitespace-nowrap align-top w-1/3">
                                                {row.name}
                                            </th>
                                            <td className="px-5 py-3.5 text-[#212529]/75 leading-relaxed">
                                                {row.value}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-[#212529]/50 mt-4">
                            Figures reflect the current prototype and may evolve as development
                            progresses. Read more about the design in{" "}
                            <Link to="/axial-flux-vs-radial-flux" className="underline hover:text-[#212529]">
                                axial flux vs. radial flux motors
                            </Link>{" "}
                            or on the{" "}
                            <Link to="/technology" className="underline hover:text-[#212529]">
                                technology page
                            </Link>.
                        </p>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section
                ref={ctaRef}
                className="py-16"
            >
                <div className="container mx-auto px-4 text-center">
                    <div
                        className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                            <h3 className="text-2xl md:text-3xl font-medium mb-4 text-[#212529]">
                                Ready to Experience the Future of Electric Motors?
                            </h3>
                            <p className="text-[#212529]/80 mb-8">
                                Contact our team today to discover how our revolutionary axial flux motor technology can transform your applications.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-primary/90 hover:bg-primary text-white backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Contact Us
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                                <Link
                                    to="/technology"
                                    className="bg-white/70 hover:bg-white/80 text-[#212529] backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Learn More About Our Technology
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ExploreMotor;
